import * as React from 'react';
import * as Redux from 'redux';
import qs from "query-string";
import { MainState } from '../../Core/reducers';
import { CoughSessionsListTable } from './CoughSessionsListTable';
import DocumentTitle from '../../misc/components/DocumentTitle';
import { Filter, ODataParams, Query } from '../../Components/Query';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { Navigate, Route, useLocation, Routes } from 'react-router-dom';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { CoughAnalysisScreen } from '../../cough-analysis/components/CoughAnalysisScreen';
import { AnalyseAppProvider } from '../analyse';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { useSecurityContext } from '../../Core/SecurityContext';
import { HasPermission } from '../../misc/components/Authorization';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { PrivateRoute } from '../../Core/PrivateRoute';

export interface CoughSessionsListScreenProps {
    dispatch?: Redux.Dispatch<MainState>;
}

export const CoughSessionsListScreen = (props: CoughSessionsListScreenProps) => {
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();
    const location = useLocation();
    const [initialFilters, setInitialFilters] = React.useState<Filter[]>(undefined);
    const [initialAnalysisFilters, setInitialAnalysisFilters] = React.useState<Filter[]>(undefined);
    const { currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        const queryString = qs.parse(location.search);

        if (queryString.statusId) {
                setInitialFilters([
                    {
                        field: "status",
                        condition: "eq",
                        values: [queryString.statusId],
                    }
                ]);
        } else {
            setInitialFilters([]);
        }
    }, [location.search]);

    const handleRefresh = async (params: ODataParams) => {
        let filter = params.filter ? `(${params.filter}) and ` : '';
        filter += `visit/subject/site/studyId eq ${study.id}`;

        return await client.client.getCoughSessions("visit,analysisSets,id,name,serialNumber,deviceType,recordingStart,lastReceived,lastAnalysed,status,suppressed,postalTrackingNumber,postalReceived,configured",
            filter,
            params.orderby,
            'visit($expand=subject($expand=site($select=id,number))),analysisSets($select=id,status,statusName,typeName,created)',
            params.top,
            params.skip,
            undefined,
            true
        );
    }

    const CoughAnalysisPage = () =>{
        if (!HasPermission(currentUserPermissions, study?.id, PermissionEnum.ViewSingleCoughSession))
        {
            return(
                <Navigate to={{ pathname: "/unauthorized" }} />
            );
        }
        return <AnalyseAppProvider />
    }

    if (!initialFilters)
        return <LoadingWithMessage message="Loading Cough Sessions..." />

    return (
        <Routes>
            <Route index element={<PrivateRoute permissions={[PermissionEnum.ViewAllCoughSessions]}>
              <DocumentTitle title="Cough Sessions | Vitalograph Web Portal">
                <Query initialFilters={initialFilters} initialOrdering={[{ field: "recordingStart", condition: "asc" }]} onRefresh={handleRefresh}>
                  <CoughSessionsListTable />
                </Query>
              </DocumentTitle>
            </PrivateRoute>} />
            <Route path="Analysis" element={<PrivateRoute permissions={[PermissionEnum.AccessAllCoughAnalysisSets, PermissionEnum.AccessAssignedCoughAnalysisSets, PermissionEnum.AccessUnassignedCoughAnalysisSets, PermissionEnum.AccessCoughAnalysisSetsInSessionQA]}>
              <CoughAnalysisScreen filters={initialAnalysisFilters} onFiltersApplied={setInitialAnalysisFilters} />
            </PrivateRoute>} />
            <Route path="Analyse/:primarySetId/:isViewOnly?" element={<CoughAnalysisPage />} />
        </Routes>
    );
};
